import * as Sentry from '@sentry/react';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ludolio/shared-ui';
import { Badge } from '@ludolio/shared-ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ludolio/shared-ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ludolio/shared-ui';
import { Download, TrendingUp, Users } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { apiClient } from '@/services/api';
import type { Unit } from '@/types';

export function InstallTimeView() {
  const [units, setUnits] = useState<Unit[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<string>('');
  const [statistics, setStatistics] = useState<Array<{
    gameId: string;
    gameTitle: string;
    totalPlayingTimeSeconds: number;
    totalSessions: number;
    uniquePlayersCount: number;
    installationCount: number;
  }>>([]);
  const [_loading, setLoading] = useState(true);

  useEffect(() => {
    loadUnits();
  }, []);

  useEffect(() => {
    if (selectedUnitId) {
      loadStatistics();
    }
  }, [selectedUnitId]);

  const loadUnits = async () => {
    try {
      const response = await apiClient.getMyUnits();
      if (response.success && response.data) {
        setUnits(response.data);
        if (response.data.length > 0) {
          setSelectedUnitId(response.data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load units:', error);
      Sentry.captureException(error);
    }
  };

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getUnitStatistics(selectedUnitId);
      if (response.success && response.data) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
      Sentry.captureException(error);
    } finally {
      setLoading(false);
    }
  };

  const totalInstalls = statistics.reduce((sum, stat) => sum + stat.installationCount, 0);
  const averageInstallsPerGame = statistics.length > 0 ? Math.round(totalInstalls / statistics.length) : 0;
  const maxInstalls = statistics.length > 0 ? Math.max(...statistics.map(stat => stat.installationCount)) : 0;

  const installCountByGame = statistics.map((stat) => ({
    name: stat.gameTitle,
    installCount: stat.installationCount,
    students: stat.uniquePlayersCount,
  }));

  const topGamesByInstalls = [...installCountByGame]
    .filter(g => g.installCount > 0)
    .sort((a, b) => b.installCount - a.installCount)
    .slice(0, 5);

  // Student install counts (empty for now - requires per-student tracking)
  const studentInstallCounts: any[] = [];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Installation Count</h1>
        <div className="w-full sm:w-64">
          <Select value={selectedUnitId} onValueChange={setSelectedUnitId}>
            <SelectTrigger aria-label="Select unit for installation data">
              <SelectValue placeholder="Select a unit" />
            </SelectTrigger>
            <SelectContent>
              {units.map((unit) => (
                <SelectItem key={unit.id} value={unit.id}>
                  {unit.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Installs</CardTitle>
            <Download className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalInstalls}</div>
            <p className="text-xs text-muted-foreground">Across all games</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average per Game</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageInstallsPerGame}</div>
            <p className="text-xs text-muted-foreground">Per game average</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Most Installed</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{maxInstalls}</div>
            <p className="text-xs text-muted-foreground">Max installs</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="by-game">By Game</TabsTrigger>
          <TabsTrigger value="by-student">By Student</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-6">
          <div className="grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2">
            {/* Install Count by Game */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Install Count by Game</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Number of times each game was installed</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="w-full overflow-x-auto">
                  <ResponsiveContainer width="100%" height={300} minWidth={300}>
                    <BarChart data={installCountByGame.filter(g => g.installCount > 0)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} fontSize={10} />
                      <YAxis fontSize={10} />
                      <Tooltip />
                      <Bar dataKey="installCount" fill="#8b5cf6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Install Count by Student */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Install Count by Student</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Total installations per student</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="w-full overflow-x-auto">
                  <ResponsiveContainer width="100%" height={300} minWidth={300}>
                    <BarChart data={studentInstallCounts}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} fontSize={10} />
                      <YAxis fontSize={10} />
                      <Tooltip />
                      <Bar dataKey="installCount" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="by-game" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Games by Install Count</CardTitle>
              <CardDescription>Most installed games</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {topGamesByInstalls.map((game, index) => (
                  <div key={game.name} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-muted-foreground">#{index + 1}</span>
                          <h4 className="font-semibold text-sm">{game.name}</h4>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{game.students} students</p>
                      </div>
                      <Badge variant="secondary">{game.installCount} installs</Badge>
                    </div>
                    <div className="bg-secondary rounded p-3">
                      <p className="text-xs text-muted-foreground">Total Installations</p>
                      <p className="text-lg font-semibold">{game.installCount}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="by-student" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Students by Install Count</CardTitle>
              <CardDescription>Total installations per student</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {studentInstallCounts.map((student, index) => (
                  <div key={student.name} className="flex items-center gap-4 p-3 border rounded-lg">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-lg font-bold text-muted-foreground w-6">#{index + 1}</span>
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-xs font-semibold text-[hsl(var(--color-primary-text))]">{student.name.charAt(0)}</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{student.name}</p>
                        <p className="text-xs text-muted-foreground">{student.gamesInstalled} games</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{student.installCount} installs</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

